import React from "react";
import { compose } from "redux";
import { useSelector, useDispatch } from "react-redux";
import { withNoCaching } from "helpers/cache-control-helpers";
import Instrumentation from "helpers/instrumentation";
import { ProductGridItems } from "components/source/grid/product-grid-items";
import Grid from "components/source/grid/grid";
import { DiscoSearchServiceClient } from "clients/DiscoSearchServiceClient";
import { GridContainer } from "components/source/grid/grid-container";
import GridNav from "../../../assets/javascripts/components/source/grid/grid-nav";
import PropTypes from "prop-types";
import NoProducts from "../../../assets/javascripts/components/source/new_taxonomy/no-products";

import {
  addUserId,
  appendFilters,
  DEFAULT_FILTERS,
  getFilterOptions,
  mapFilters,
  mapGridQueryToDiscoQuery,
  mapGridQueryToWorkingFilters,
  parseQueryString,
  setFilterIfMissing,
  sortFilterValuesAlphabetically,
} from "helpers/disco-filter-helpers";
import productsActions from "actions/products-actions";
import { sortKeys, NEXT_GRID_TYPES, HIDDEN_FILTERS, FILTERS_TO_PRESERVE } from "rtr-constants";
import styles from "./styles.module.css";
import {
  getGridFooterContent,
  isGridTopperContentPresent,
  getGridTopperContent,
  parseVisualNavContent,
} from "../../../assets/javascripts/helpers/next-cms-helpers";
import CmsServiceClient from "../../../assets/javascripts/clients/CmsServiceClient";
import { getRequestSessionUserId } from "helpers/server-side-only/storefront-migration-helpers";
import { fetchMembershipData } from "helpers/server-side-only/next-grid-ssr-helpers";
import { objectSerializationHelper } from "helpers/next-helpers";
import { getMultipleFlags } from "../../../assets/javascripts/helpers/server-side-only/flag-helpers";
import { flagsAndExperimentNames } from "../../../assets/javascripts/constants";

const PAGE_TYPE = "grid-curation";
const LIMIT = 60;
// TODO: Move to constants

const propTypes = {};

export const getServerSideProps = async context => {
  const {
    res,
    req,
    params: { curationId },
  } = context;

  Instrumentation.tagPage(PAGE_TYPE);

  const [notifyMeFlag] = await getMultipleFlags([flagsAndExperimentNames.SF_SACT_250_NOTIFY_ME], req, res);

  const userId = getRequestSessionUserId(req);

  const { appendMembershipFilters, lens, membershipState } = await fetchMembershipData(req, res);
  const query = compose(
    appendMembershipFilters,
    addUserId(userId),
    appendFilters({ curation: [curationId] }),
    setFilterIfMissing("size", req.cookies.canonicalSizes?.split(",")),
    appendFilters(DEFAULT_FILTERS[req.url.split("?")[0]]),
    mapFilters(NEXT_GRID_TYPES["curation"]),
    parseQueryString
  )(req.url);

  const { buynow, page = 1 } = query;

  withNoCaching(res);

  const cmsUrl = req.url.split("?")[0];
  const cmsServiceClient = CmsServiceClient.getInstance();

  const [
    gridTopperContent,
    gridFooterContent,
    clientResponse,
    visualNav,
    visualNavMembership,
    visualNavReserve,
    visualNavContentNoIntent,
  ] = await Promise.all([
    getGridTopperContent(context),
    getGridFooterContent(context),
    DiscoSearchServiceClient.getInstance().curationSearch(
      mapGridQueryToDiscoQuery(query, NEXT_GRID_TYPES["curation"]),
      parseInt(page),
      LIMIT,
      lens
    ),
    cmsServiceClient.getAllContent("/visual-nav"),
    cmsServiceClient.getAllContent("/visual-nav/membership"),
    cmsServiceClient.getAllContent("/visual-nav/reserve"),
    cmsServiceClient.getAllContent("/visual-nav/no-intent"),
  ]);

  // Get filter counts
  const informedFilters = clientResponse.filters;

  // Determine assortment
  const assortment = clientResponse?.meta?.assortment || [];

  // Determine initial filter options
  const filterOptions = getFilterOptions(assortment);

  return {
    props: {
      initialReduxState: {
        filterOptions: sortFilterValuesAlphabetically(filterOptions),
        informedFilters,
        assortment: assortment,
        gridFooterContent: {
          contentModules: gridFooterContent?.contentModules || [],
          fromCache: gridFooterContent?.contentModules?.some(cm => cm.fromCache) || false,
        },
        workingFilters: mapGridQueryToWorkingFilters(query),
        gridTopperContent: {
          contentModules: gridTopperContent?.contentModules || [],
          fromCache: gridTopperContent?.contentModules?.some(cm => cm.fromCache) || false,
        },
        nextGridType: NEXT_GRID_TYPES["curation"],
        gridMetaData: clientResponse?.meta || {},
        gridType: "GRID", // TODO: update gridType based on lens
        gridView: {
          isUnlimited: false,
          isRtrUpdate: false,
          isAllMemberships: false,
          isBuyNow: buynow === "true",
          isClearance: false,
          // Need `isRentable: true` to get the GridTopFiltersComponent
          // to render the "Choose how you rent?" dropdown
          isRentable: true,
          isDepot: false,
          isKids: null,
          isHomeGood: null,
          isPersonalizedGrid: false,
        },
        isBuyNow: buynow === "true",
        hideGridHeader: isGridTopperContentPresent(gridTopperContent), // if gridTopper is present hide header
        path: cmsUrl,
        gridProducts: clientResponse.items,
        gridCount: clientResponse.count.total ?? 0,
        loading: true,
        membershipState: objectSerializationHelper(membershipState), // idk why when I plugin membershipState here it says `object`` cannot be serialized as JSON, hence this hack
        workingSort: query.sort || sortKeys.NEWEST,
        currentPage: page,
        visualNav: parseVisualNavContent(visualNav),
        visualNavMembership: parseVisualNavContent(visualNavMembership),
        visualNavReserve: parseVisualNavContent(visualNavReserve),
        visualNavContentNoIntent: parseVisualNavContent(visualNavContentNoIntent),
        userData: {
          isClassicLens: lens === "classic",
          isUnlimitedLens: lens === "unlimited",
          isRTRUpdateLens: lens === "rtrupdate",
          lens: lens || null,
        },
        url: req.url,
        itemLimit: LIMIT,
        flagsAndExperiments: {
          [flagsAndExperimentNames.SF_SACT_250_NOTIFY_ME]: notifyMeFlag?.value,
        },
      },
    },
  };
};

export default function GridsPage() {
  const dispatch = useDispatch();
  const gridCount = useSelector(state => state.gridCount);
  const gridProducts = useSelector(state => state.gridProducts);
  const workingFilters = useSelector(state => state.workingFilters);
  const nextGridType = useSelector(state => state.nextGridType);
  const totalPages = Math.ceil(gridCount / LIMIT);

  const clearAllFilters = () => {
    const filtersToPreserve = [...FILTERS_TO_PRESERVE];
    if (nextGridType in (HIDDEN_FILTERS || {})) filtersToPreserve.push(HIDDEN_FILTERS[nextGridType][0]);
    const filtersToClear = Object.keys(workingFilters).filter(filterKey => !filtersToPreserve.includes(filterKey));
    dispatch(productsActions.clearFilters(filtersToClear));
  };

  return (
    <React.Fragment>
      <GridContainer>
        <div className={`${styles["products-grid"]}`}>
          {gridProducts?.length > 0 ? (
            <Grid>
              <ProductGridItems />
            </Grid>
          ) : (
            <div className="grid-no-products">
              <NoProducts optionalButtonText="Browse All" optionalButtonAction={clearAllFilters} />
            </div>
          )}
          <GridNav totalPages={totalPages} />
        </div>
      </GridContainer>
    </React.Fragment>
  );
}

GridsPage.pageName = PAGE_TYPE;
GridsPage.pageType = PAGE_TYPE;
GridsPage.pageSize = PropTypes.number;
GridsPage.propTypes = propTypes;
GridsPage.getCssChunkNames = () => ["application-grid", "grid"]; // yup, this is a thing
GridsPage.getHeadData = (pageProps, url) => {
  return url ? { canonical: url.split("?")[0].replace("/pdp/shop", "/shop") } : {};
};
